import { useState } from 'react';
import { Button, Field, Input, Spinner, Text, makeStyles, tokens } from '@fluentui/react-components';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useT } from '../i18n';
import { useFsmaEnabled } from '../hooks/useFsmaEnabled';
import { apiErrorMessage } from '../api/apiError';
import {
  exportFsmaSheet,
  traceFsmaTlc,
  listRecallDrills,
  runRecallDrill,
  type TraceResult,
} from '../api/traceability.api';
import { PageTitle } from '../components/shared/PageTitle';

const DRILLS_KEY = ['recall-drills'] as const;

const useStyles = makeStyles({
  page:    { display: 'flex', flexDirection: 'column', gap: '16px', padding: '16px', maxWidth: '760px' },
  section: { display: 'flex', flexDirection: 'column', gap: '10px', paddingBottom: '12px', borderBottom: `1px solid ${tokens.colorNeutralStroke2}` },
  row:     { display: 'flex', flexWrap: 'wrap', gap: '8px', alignItems: 'flex-end' },
  muted:   { color: tokens.colorNeutralForeground3 },
});

function downloadBase64(fileName: string, fileBase64: string, contentType: string) {
  const bytes = Uint8Array.from(atob(fileBase64), (ch) => ch.charCodeAt(0));
  const url = URL.createObjectURL(new Blob([bytes], { type: contentType }));
  const a = document.createElement('a');
  a.href = url;
  a.download = fileName;
  a.click();
  URL.revokeObjectURL(url);
}

/** Convierte un valor de datetime-local (hora local) a ISO 8601. */
const toIso = (local: string) => (local ? new Date(local).toISOString() : '');

export default function FsmaExportPage() {
  const t = useT();
  const c = t.fsmaExport;
  const s = useStyles();
  const enabled = useFsmaEnabled();
  const qc = useQueryClient();

  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [exportTlc, setExportTlc] = useState('');
  const [traceTlc, setTraceTlc] = useState('');
  const [trace, setTrace] = useState<TraceResult | null>(null);
  const [drillTlc, setDrillTlc] = useState('');
  const [drillStart, setDrillStart] = useState('');
  const [drillFinish, setDrillFinish] = useState('');
  const [drillMerma, setDrillMerma] = useState('');
  const [drillNotes, setDrillNotes] = useState('');

  const drills = useQuery({ queryKey: DRILLS_KEY, queryFn: listRecallDrills, enabled });

  const exportMut = useMutation({
    mutationFn: async (format: 'xlsx' | 'csv') => {
      const params: Parameters<typeof exportFsmaSheet>[0] = { format };
      if (from) params.from = from;
      if (to) params.to = to;
      if (exportTlc.trim()) params.tlc = exportTlc.trim();
      const file = await exportFsmaSheet(params);
      downloadBase64(file.fileName, file.fileBase64, file.contentType);
      // true cuando la hoja se cortó en el tope de registros: aviso, no error.
      return file.truncated === true;
    },
  });

  const traceMut = useMutation({
    mutationFn: (direction: 'forward' | 'backward') => traceFsmaTlc(traceTlc.trim(), direction),
    onSuccess: (res) => setTrace(res),
  });

  const drillMut = useMutation({
    mutationFn: () => runRecallDrill({
      tlc: drillTlc.trim(),
      startedAt: toIso(drillStart),
      finishedAt: toIso(drillFinish),
      mermaKg: drillMerma ? Number(drillMerma) : undefined,
      notes: drillNotes.trim() || undefined,
    }),
    onSuccess: () => qc.invalidateQueries({ queryKey: DRILLS_KEY }),
  });

  if (!enabled) {
    return (
      <div className={s.page}>
        <PageTitle>{c.title}</PageTitle>
        <Text data-testid="fsma-disabled" className={s.muted}>{c.disabled}</Text>
      </div>
    );
  }

  const drillResult = drillMut.data;
  const passedLabel = drillResult
    ? drillResult.passed === true ? c.drillPassed
      : drillResult.passed === false ? c.drillFailed
      : c.drillNoThreshold
    : '';

  return (
    <div className={s.page}>
      <div>
        <PageTitle>{c.title}</PageTitle>
        <Text as="p" className={s.muted}>{c.subtitle}</Text>
      </div>

      <section className={s.section}>
        <Text as="h2" size={500} weight="semibold">{c.exportTitle}</Text>
        <div className={s.row}>
          <Field label={c.rangeFrom}>
            <Input data-testid="fsma-from" type="date" value={from} onChange={(_, d) => setFrom(d.value)} />
          </Field>
          <Field label={c.rangeTo}>
            <Input data-testid="fsma-to" type="date" value={to} onChange={(_, d) => setTo(d.value)} />
          </Field>
          <Field label={c.tlcOptional}>
            <Input data-testid="fsma-export-tlc" value={exportTlc} onChange={(_, d) => setExportTlc(d.value)} />
          </Field>
        </div>
        <div className={s.row}>
          <Button data-testid="fsma-export-xlsx" appearance="primary" onClick={() => exportMut.mutate('xlsx')} disabled={exportMut.isPending}>
            {c.exportXlsx}
          </Button>
          <Button data-testid="fsma-export-csv" onClick={() => exportMut.mutate('csv')} disabled={exportMut.isPending}>
            {c.exportCsv}
          </Button>
        </div>
        {exportMut.isError && <Text role="alert">{apiErrorMessage(exportMut.error, c.exportError)}</Text>}
        {exportMut.data === true && <Text role="status" data-testid="fsma-export-truncated">{c.exportTruncated}</Text>}
      </section>

      <section className={s.section}>
        <Text as="h2" size={500} weight="semibold">{c.traceTitle}</Text>
        <div className={s.row}>
          <Field label={c.traceTlc}>
            <Input data-testid="fsma-trace-tlc" value={traceTlc} onChange={(_, d) => setTraceTlc(d.value)} />
          </Field>
          <Button data-testid="fsma-trace-forward" onClick={() => traceMut.mutate('forward')} disabled={!traceTlc.trim() || traceMut.isPending}>
            {c.traceForward}
          </Button>
          <Button data-testid="fsma-trace-backward" onClick={() => traceMut.mutate('backward')} disabled={!traceTlc.trim() || traceMut.isPending}>
            {c.traceBackward}
          </Button>
        </div>
        {traceMut.isPending && <Spinner size="tiny" />}
        {traceMut.isError && <Text role="alert">{apiErrorMessage(traceMut.error, c.traceError)}</Text>}
        {trace && (
          <div data-testid="fsma-trace-result">
            <Text as="p">{c.lotesLabel}: {trace.lotes.join(', ') || c.none}</Text>
            <Text as="p">{c.foliosLabel}: {trace.folios.join(', ') || c.none}</Text>
            <Text as="p">{c.unmatchedLabel}: {trace.unmatchedKg} kg</Text>
            {trace.shipments.length === 0 ? (
              <Text as="p" className={s.muted}>{c.noShipments}</Text>
            ) : (
              <ul>
                {trace.shipments.map((sh) => (
                  <li key={sh.shipmentId}>
                    {sh.shipmentId} · {sh.buyer ?? c.none} · {sh.shipDate ?? c.none} · {sh.kg ?? c.none}
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}
      </section>

      <section className={s.section}>
        <Text as="h2" size={500} weight="semibold">{c.drillTitle}</Text>
        <div className={s.row}>
          <Field label={c.traceTlc}>
            <Input data-testid="fsma-drill-tlc" value={drillTlc} onChange={(_, d) => setDrillTlc(d.value)} />
          </Field>
          <Field label={c.drillStart}>
            <Input data-testid="fsma-drill-start" type="datetime-local" value={drillStart} onChange={(_, d) => setDrillStart(d.value)} />
          </Field>
          <Field label={c.drillFinish}>
            <Input data-testid="fsma-drill-finish" type="datetime-local" value={drillFinish} onChange={(_, d) => setDrillFinish(d.value)} />
          </Field>
          <Field label={c.drillMerma}>
            <Input data-testid="fsma-drill-merma" type="number" min={0} value={drillMerma} onChange={(_, d) => setDrillMerma(d.value)} />
          </Field>
          <Field label={c.drillNotes}>
            <Input data-testid="fsma-drill-notes" value={drillNotes} onChange={(_, d) => setDrillNotes(d.value)} />
          </Field>
        </div>
        <Button
          data-testid="fsma-drill-submit"
          onClick={() => drillMut.mutate()}
          disabled={!drillTlc.trim() || !drillStart || !drillFinish || drillMut.isPending}
        >
          {c.drillSubmit}
        </Button>
        {drillMut.isError && <Text role="alert">{apiErrorMessage(drillMut.error, c.drillError)}</Text>}
        {drillResult && (
          <div data-testid="fsma-drill-result">
            <Text as="p">{c.drillMinutes}: {drillResult.elapsedMinutes} min</Text>
            <Text as="p">{c.unmatchedLabel}: {drillResult.unaccountedKg} kg ({drillResult.unaccountedPct}%)</Text>
            <Text as="p" weight="semibold">{passedLabel}</Text>
          </div>
        )}

        {drills.isLoading && <Spinner size="tiny" />}
        {drills.data && drills.data.length > 0 && (
          <div>
            <Text as="h3" weight="semibold">{c.drillsHistory}</Text>
            <ul>
              {drills.data.map((d) => (
                <li key={d.drillId}>{d.tlc} · {d.elapsedMinutes} min · {d.createdAt.slice(0, 10)}</li>
              ))}
            </ul>
          </div>
        )}
      </section>
    </div>
  );
}
